import React, { useState } from "react"
import { Link, NavLink, useHistory, useLocation } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import "./dashboard.css"

const NAV = [
  { to: "/dashboard", label: "Overview", icon: "fa-solid fa-chart-pie", exact: true },
  { to: "/dashboard/saved-searches", label: "Saved searches", icon: "fa-solid fa-magnifying-glass-location", exact: false },
  { to: "/dashboard/saved-homes", label: "Saved homes", icon: "fa-solid fa-heart", exact: false },
  { to: "/dashboard/applications", label: "Applications", icon: "fa-solid fa-file-signature", exact: false },
  { to: "/dashboard/bookings", label: "Bookings", icon: "fa-solid fa-calendar-check", exact: false },
  { to: "/dashboard/profile", label: "Profile", icon: "fa-solid fa-user-gear", exact: false },
]

const PATH_TITLES = {
  "/dashboard": "Overview",
  "/dashboard/saved-searches": "Saved searches",
  "/dashboard/saved-homes": "Saved homes",
  "/dashboard/applications": "Applications",
  "/dashboard/bookings": "Bookings",
  "/dashboard/profile": "Profile",
}

const PATH_DESCRIPTIONS = {
  "/dashboard": "Track your saved homes, searches, bookings, and applications in one calm workspace.",
  "/dashboard/saved-searches": "Reopen your favorite search filters and continue comparing the market.",
  "/dashboard/saved-homes": "Review the homes you have saved and jump back into property details.",
  "/dashboard/applications": "Follow the progress of your submitted rental applications.",
  "/dashboard/bookings": "Manage tours, stays, and booking requests from your account.",
  "/dashboard/profile": "Keep your contact details fresh so the Vista Hill team can reach you.",
}

function getInitials(user) {
  const name = user?.name || user?.email || "User"
  return name
    .split(/\s|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "U"
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth()
  const history = useHistory()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const title = PATH_TITLES[location.pathname] || "Dashboard"
  const description = PATH_DESCRIPTIONS[location.pathname] || "Manage your Vista Hill account."

  const closeSidebar = () => setSidebarOpen(false)

  const handleLogout = () => {
    logout()
    history.push("/")
  }

  return (
    <div className='dashboard-layout'>
      <div
        className={`dashboard-sidebar__backdrop ${sidebarOpen ? "dashboard-sidebar--open" : ""}`}
        onClick={closeSidebar}
        aria-hidden='true'
      />

      <aside className={`dashboard-sidebar ${sidebarOpen ? "dashboard-sidebar--open" : ""}`}>
        <div className='dashboard-sidebar__brand'>
          <Link to='/' onClick={closeSidebar}>
            <img
              src='/images/vista-logo-light.png'
              alt='Vista Hill'
              className='dashboard-sidebar__logo-img'
            />
              {/* <small>User portal</small> */}
          </Link>
        </div>

        <nav className='dashboard-sidebar__nav' aria-label='Dashboard navigation'>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              exact={item.exact}
              activeClassName='dashboard-sidebar__nav--active'
              onClick={closeSidebar}
            >
              <i className={item.icon}></i>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className='dashboard-sidebar__footer'>
          <Link to='/listings' onClick={closeSidebar}>
            <i className='fa-solid fa-house-chimney-window'></i>
            Browse listings
          </Link>
          {user?.role === "admin" && (
            <Link to='/admin' onClick={closeSidebar}>
              <i className='fa-solid fa-lock'></i>
              Admin workspace
            </Link>
          )}
        </div>
      </aside>

      <div className='dashboard-main'>
        <header className='dashboard-header'>
          <button
            type='button'
            className='dashboard-sidebar__toggle'
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label='Toggle dashboard menu'
          >
            <i className='fa-solid fa-bars'></i>
          </button>

          <div className='dashboard-header__copy'>
            <span>Dashboard</span>
            <h1>{title}</h1>
            <p>{description}</p>
          </div>

          <div className='dashboard-header__user'>
            <div className='dashboard-avatar' aria-hidden='true'>
              {user?.profile?.avatar ? <img src={user.profile.avatar} alt='' /> : getInitials(user)}
            </div>
            <div className='dashboard-header__identity'>
              <strong>{user?.name || "Welcome"}</strong>
              <span>{user?.email}</span>
            </div>
            <button type='button' onClick={handleLogout}>
              <i className='fa-solid fa-arrow-right-from-bracket'></i>
              <span>Sign out</span>
            </button>
          </div>
        </header>

        <main className='dashboard-content'>{children}</main>
      </div>
    </div>
  )
}
